import { format, getDay, parse, startOfDay, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import React, { useEffect, useRef, useState } from 'react';
import { Calendar, DateCellWrapperProps, dateFnsLocalizer, SlotInfo } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Link, useNavigate } from 'react-router-dom';
import { displaySessionTimeRange } from '../../../utils/dateUtils';
import Toolbar from './CustomComponents/Toolbar';
import { CalendarTraineeObserverNote } from './CustomComponents/CalendarTraineeObserverNote';
import { createEventStyleGetterWithInstructor, Event } from './SFCCalendar';

const locales = {
  'en-US': enUS
};

// Create the localizer
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});

interface CustomDayProp {
  date?: Date;
  events: Event[];
  admin?: boolean;
}

const CustomDayCell: React.FC<CustomDayProp & { filteredInstructorId?: number | null }> = ({
  date,
  events,
  admin,
  filteredInstructorId
}) => {
  const cellDateKey = date ? format(date, 'yyyy-MM-dd') : '';
  const filteredEvents = events.filter((event) => {
    const eventDateKey = event.lesson?.date
      ? String(event.lesson.date).slice(0, 10)
      : format(event.start, 'yyyy-MM-dd');
    return eventDateKey === cellDateKey;
  });

  const eventStyleGetterWithInstructor = createEventStyleGetterWithInstructor(filteredInstructorId);

  return filteredEvents.length > 0 ? (
    <div
      className="d-flex justify-content-evenly align-items-end h-100"
      style={{ marginBottom: 0 }}
    >
      {admin ? (
        <div
          className="bg-primary"
          style={{
            ...eventStyleGetterWithInstructor(filteredEvents[0]).style,
            width: 5,
            height: 5
          }}
        />
      ) : (
        filteredEvents.slice(0, 5).map((ev: Event, idx: number) => {
          const { style, pendingSubRequest } = eventStyleGetterWithInstructor(ev);
          const dotStyle = pendingSubRequest
            ? {
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: 'transparent',
                border: `1.5px dashed ${style.color}`,
                boxSizing: 'border-box' as const
              }
            : { ...style, width: 5, height: 5 };

          return (
            <div
              key={`${ev.title}-${idx}`}
              style={dotStyle}
            />
          );
        })
      )}
    </div>
  ) : (
    <></>
  );
};

interface CustomDayCellWrapperProps extends DateCellWrapperProps {
  admin: boolean;
  selected: boolean;
  events: Event[];
  filteredInstructorId?: number | null;
}

const CustomDayCellWrapper: React.FC<
  CustomDayCellWrapperProps & { onDayClick?: (date: Date) => void }
> = ({ value, children, admin, selected, events, filteredInstructorId, onDayClick }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDayClick) {
      onDayClick(value);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDayClick) {
      onDayClick(value);
    }
  };

  return (
    <div
      className={`${children.props.className} ${selected ? 'selected-slot' : ''}`}
      style={{
        zIndex: 10,
        cursor: 'pointer',
        position: 'relative',
        height: '100%',
        width: '100%'
      }}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
    >
      <CustomDayCell
        date={value}
        events={events}
        admin={admin}
        filteredInstructorId={filteredInstructorId}
      />
      {children}
    </div>
  );
};

interface SFCMobileCalendarProps {
  events: Event[];
  showFullEvents?: boolean;
  currentDates?: string[];
  filteredInstructorId?: number | null;
}

export const SFCMobileCalendar: React.FC<SFCMobileCalendarProps> = ({
  events,
  showFullEvents,
  currentDates,
  filteredInstructorId
}) => {
  const [dayEvents, setDayEvents] = useState<Event[]>([]);
  const [dayName, setDayName] = useState(`Today's`);
  // Calendar-day comparisons only — do not re-zone via dateInTimeZone (shifts days
  // when profile TZ ≠ device TZ). Same approach as SchedulerCalendar.
  const [selectedSlot, setSelectedSlot] = useState(startOfDay(new Date()));
  const scheduleRef = useRef<HTMLDivElement>(null);
  const shouldScrollToScheduleRef = useRef(false);
  const navigate = useNavigate();

  const scrollScheduleIntoView = () => {
    const scheduleEl = scheduleRef.current;
    if (!scheduleEl) return;

    const rect = scheduleEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const elementCenter = rect.top + rect.height / 2;
    const viewportCenter = viewportHeight / 2;
    const offsetFromCenter = elementCenter - viewportCenter;

    // Skip small adjustments so the scroll feels less aggressive.
    const SCROLL_THRESHOLD = 120;
    if (Math.abs(offsetFromCenter) < SCROLL_THRESHOLD) return;

    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const targetTop = Math.max(0, currentScrollTop + offsetFromCenter);

    window.scrollTo({
      top: targetTop,
      behavior: 'smooth'
    });
  };

  const eventCalendarDateKey = (ev: Event) =>
    ev.lesson?.date
      ? String(ev.lesson.date).slice(0, 10)
      : format(ev.start, 'yyyy-MM-dd');

  useEffect(() => {
    const selectedDateKey = format(selectedSlot, 'yyyy-MM-dd');

    const filteredEvents = events
      .filter((ev) => eventCalendarDateKey(ev) === selectedDateKey)
      .sort((a, b) => a.start.getTime() - b.start.getTime());

    setDayEvents(filteredEvents);
  }, [events, selectedSlot]);

  useEffect(() => {
    if (!showFullEvents || !shouldScrollToScheduleRef.current) return;

    shouldScrollToScheduleRef.current = false;
    requestAnimationFrame(() => {
      scrollScheduleIntoView();
    });
  }, [selectedSlot, dayEvents, showFullEvents]);

  const onSelectSlot = (slotInfo: SlotInfo) => {
    if (!showFullEvents) {
      navigate(
        `/admin/schedule/classes?starts_on=${currentDates?.[0]}&ends_on=${currentDates?.[1]}`
      );
      return;
    }

    const normalizedSelectedSlot = startOfDay(slotInfo.start);

    setSelectedSlot(normalizedSelectedSlot);

    const todayKey = format(new Date(), 'yyyy-MM-dd');
    const selectedKey = format(normalizedSelectedSlot, 'yyyy-MM-dd');
    const dayName =
      selectedKey === todayKey ? `Today's` : format(normalizedSelectedSlot, 'EEEE dd');

    setDayName(dayName);

    shouldScrollToScheduleRef.current = true;
  };

  const handleDayClick = (date: Date) => {
    const slotInfo: SlotInfo = {
      start: date,
      end: date,
      slots: [date],
      action: 'select'
    };
    onSelectSlot(slotInfo);
  };

  return (
    <>
      <Calendar<Event>
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        views={['month']} // Only show the month view
        defaultView="month" // Set the default view to month
        style={{ height: 400, background: '#FFFFFF' }}
        onNavigate={(date) => {
          console.log('onNavigate triggered with date:', date);
        }}
        components={{
          toolbar: Toolbar,
          dateCellWrapper: (props) => (
            <CustomDayCellWrapper
              {...props}
              events={events}
              admin={false}
              selected={selectedSlot.toDateString() === props.value.toDateString()}
              filteredInstructorId={filteredInstructorId}
              onDayClick={handleDayClick}
            />
          ),
          month: {
            header: (props) => <>{localizer.format(props.date, 'EE')}</>
          }
        }}
        selectable={false}
        onSelectSlot={onSelectSlot}
        onSelectEvent={(event) => {
          console.log('Event selected:', event);
        }}
        eventPropGetter={createEventStyleGetterWithInstructor(filteredInstructorId)}
      />
      {showFullEvents && (
        <div
          className="rounded-3 mt-3 border p-2 pb-0"
          style={{ background: '#FFFFFF' }}
          ref={scheduleRef}
        >
          <span className="text-info text-uppercase fw-semibold">{dayName} Schedule</span>
          <div className="mt-1">
            {dayEvents.length > 0 ? (
              dayEvents.map((ev: Event, index) => {
                const { style, pendingSubRequest } =
                  createEventStyleGetterWithInstructor(filteredInstructorId)(ev);
                const sessionId = ev.lesson?.course_id;
                const cardContent = (
                  <div
                    className="custom-event-cell px-2 py-1"
                    style={{ fontSize: 12 }}
                  >
                    <span className="fw-bold">{ev.title}</span>
                    <br />
                    <span>
                      {displaySessionTimeRange(
                        ev.lesson?.calculated_starts_at ?? ev.lesson?.course?.starts_at,
                        ev.lesson?.calculated_ends_at ?? ev.lesson?.course?.ends_at,
                        (ev.lesson?.course?.venue as any)?.location?.timezone
                      ) || ev.lesson?.formatted_time}
                    </span>
                    {pendingSubRequest && (
                      <>
                        <br />
                        <span style={{ fontSize: 11, fontStyle: 'italic', opacity: 0.9 }}>
                          Sub requested
                        </span>
                      </>
                    )}
                    <CalendarTraineeObserverNote
                      lesson={ev.lesson}
                      course={ev.lesson?.course}
                      viewerInstructorId={filteredInstructorId}
                    />
                  </div>
                );
                return sessionId ? (
                  <Link
                    key={index}
                    to={`/admin/schedule/sessions/${sessionId}`}
                    className="d-block text-decoration-none mb-2"
                    style={{ ...style, cursor: 'pointer' }}
                  >
                    {cardContent}
                  </Link>
                ) : (
                  <div
                    key={index}
                    className="mb-2"
                    style={style}
                  >
                    {cardContent}
                  </div>
                );
              })
            ) : (
              <div className="mb-2">No classes for {selectedSlot.toDateString()}</div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
